import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

export const useNotifications = (user) => {
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);

  // Process notification queue - show one at a time
  useEffect(() => {
    if (!currentNotification && notifications.length > 0) {
      const [next, ...rest] = notifications;
      setCurrentNotification(next);
      setNotifications(rest);
    }
  }, [notifications, currentNotification]);

  const handleAcknowledge = useCallback(() => {
    setCurrentNotification(null);
  }, []);

  const addNotification = useCallback((notification) => {
    setNotifications(prev => [...prev, notification]);
  }, []);

  useEffect(() => {
    if (!user) return;

    const connectWebSocket = () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      
      try {
        ws.current = new WebSocket(`${wsUrl}/api/ws/${user.id}`);

        ws.current.onopen = () => {
          console.log('✓ Notifications connected');
          toast.success('Connected to notifications');
        };

        ws.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'appointment_created' || data.type === 'appointment_updated' || data.type === 'appointment_completed') {
              // Add to notification queue
              addNotification(data);
            }
          } catch (error) {
            console.error('Error parsing notification:', error);
          }
        };

        ws.current.onerror = (error) => {
          console.error('WebSocket error:', error);
        };

        ws.current.onclose = () => {
          console.log('WebSocket disconnected, reconnecting...');
          reconnectTimeout.current = setTimeout(connectWebSocket, 3000);
        };
      } catch (error) {
        console.error('WebSocket connection error:', error);
      }
    };

    connectWebSocket();

    // Heartbeat to keep connection alive
    const heartbeat = setInterval(() => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(heartbeat);
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [user, addNotification]);

  return { currentNotification, handleAcknowledge, pendingCount: notifications.length };
};
